import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ReactElement } from "react";
import { PromiseStatuses } from "../../../lib/utilis/index.ts";
import { ChatBotRequestDTO, ChatBotResponseDTO, ChatDTO, FindChatBotFilterDTO, MessageFilterDTO, MessageResponse, TopicUnseenInfoDTO } from "./dto.ts";
import { NewChatService } from "./service.ts";
import { BrightyMessagesResponseDTO, FindBrightyChatByIdDTO, getAllTitlesResponseDTO, UserMessage } from "./brighty/dto.ts";

interface ChatsState {
    findChatsStatus: PromiseStatuses
    findChatResponse: ChatDTO[]
    findChatsByLoggedUserStatus: PromiseStatuses
    findChatByLoggedUserResponse: ChatDTO[]
    selectedChat: number
    findMessagesStatus: PromiseStatuses
    findMessagesResponse?: MessageResponse
    setChatSeenStatus: PromiseStatuses
    chatList?: { chat: ChatDTO, name: string, icon: ReactElement, members?: number, bot: boolean }
    usersListAfterSend: string[]
    messageFilter: MessageFilterDTO
    unSeenMessagesStatus: PromiseStatuses
    unSeenMessagesResponse?: TopicUnseenInfoDTO[]
    findLastMessageStatus: PromiseStatuses
    findLastMessageResponse?: { messages: MessageResponse; topic: number; }[]
    findChatBotStatus: PromiseStatuses
    findChatBotResponse?: BrightyMessagesResponseDTO
    findChatBotByIdStatus: PromiseStatuses
    findChatBotByIdResponse?: FindBrightyChatByIdDTO
    questionBotRequest: ChatBotRequestDTO
    questionBotStatus: PromiseStatuses
    filterChatBot: FindChatBotFilterDTO
    getAllTitlesResponse: getAllTitlesResponseDTO
    getAllBotTitlesStatus: PromiseStatuses
    deleteBotChatStatus: PromiseStatuses
}

const initialState: ChatsState = {
    findChatsStatus: 'idle',
    findChatResponse: [],
    findChatsByLoggedUserStatus: 'idle',
    findChatByLoggedUserResponse: [],
    selectedChat: 0,
    findMessagesStatus: 'idle',
    setChatSeenStatus: 'idle',
    usersListAfterSend: [],
    messageFilter: {
        page: 0,
        itemsPerPage: 0,
        order: false,
        sort: "timestamp"
    },
    unSeenMessagesStatus: 'idle',
    findLastMessageStatus: 'idle',
    findChatBotStatus: "idle",
    questionBotRequest: {
        question: "",
        userName: ""
    },
    questionBotStatus: "idle",
    filterChatBot: {
        page: 0,
        page_size: 500,
        request_user_id: "",
        financial_advisor_user_id: ""
    },
    getAllTitlesResponse: [],
    getAllBotTitlesStatus: "idle",
    findChatBotByIdStatus: "idle",
    deleteBotChatStatus: "idle"
}

export const findChats = createAsyncThunk(
    'chat/findChats',
    async (topic: string): Promise<ChatDTO[]> => {
        const chatsService = NewChatService()

        return chatsService.findChats(topic)
    },
)

export const findChatsByLoggedUser = createAsyncThunk(
    'chat/findChatsByLoggedUser',
    async (): Promise<ChatDTO[]> => {
        const chatsService = NewChatService()

        return chatsService.findChatsByLoggedUser()
    },
)

export const findLastMessage = createAsyncThunk(
    'chat/findLastMessage',
    async (request: { chatId: number, filter: MessageFilterDTO }[]): Promise<{ messages: MessageResponse, topic: number }[]> => {
        let promises: Promise<{ messages: MessageResponse, topic: number }>[] = []
        promises = request.map(async ({ chatId: id, filter: filter }) => {
            const chatsService = NewChatService()

            return chatsService.findMessages(id, filter).then(data => ({ messages: data, topic: id }))
        })
        return Promise.all(promises)
    }
)

export const findMessages = createAsyncThunk(
    'chat/findMessages',
    async (request: { chatId: number, filter: MessageFilterDTO }): Promise<MessageResponse> => {
        const chatsService = NewChatService()

        return chatsService.findMessages(request.chatId, request.filter)
    },
)

export const setChatSeen = createAsyncThunk(
    'chat/chatSeen',
    async (id: number): Promise<void> => {
        const chatsService = NewChatService()

        return chatsService.setChatSeen(id)
    },
)

export const unSeenMessages = createAsyncThunk(
    'chat/unSeen',
    async (topic: string): Promise<TopicUnseenInfoDTO[]> => {
        const chatsService = NewChatService()

        return chatsService.unSeenMessages(topic)
    },
)

export const findChatBot = createAsyncThunk(
    'chat/findChatBot',
    async (filter: FindChatBotFilterDTO): Promise<BrightyMessagesResponseDTO> => {
        const chatsService = NewChatService()

        return chatsService.findChatBot(filter)
    },
)

export const findChatBotById = createAsyncThunk(
    'chat/findChatBotById',
    async (request: { thread_id: string, user_id: string }): Promise<FindBrightyChatByIdDTO> => {
        const chatsService = NewChatService()

        return chatsService.findChatBotById(request)
    },
)

export const questionBot = createAsyncThunk(
    'chat/questionBot',
    async (request: UserMessage): Promise<void> => {
        const chatsService = NewChatService()

        return chatsService.questionBot(request)
    },
)

export const getAllBotTitles = createAsyncThunk(
    'chat/getAllTItles',
    async (): Promise<getAllTitlesResponseDTO> => {
        const chatsService = NewChatService()

        return chatsService.getAllBotTitles()
    }
)

export const deleteBotChat = createAsyncThunk(
    'chat/deleteBotChat',
    async (thread_id: string): Promise<void> => {
        const chatsService = NewChatService()

        return chatsService.deleteBotChat(thread_id)
    }
)

const chatSlice = createSlice({
    name: 'chat/slice',
    initialState,
    reducers: {
        setSelectedChat: (state, action) => {
            state.selectedChat = action.payload
        },
        setChatList: (state, action) => {
            state.chatList = action.payload
        },
        setUsersListAfterSend: (state, action) => {
            state.usersListAfterSend = action.payload
        },
        setChatSeenStatus: (state, action) => {
            state.setChatSeenStatus = action.payload
        },
        setFindChatsStatus: (state, action) => {
            state.findChatsStatus = action.payload
        },
        setFindChatsByLoggedUserStatus: (state, action) => {
            state.findChatsStatus = action.payload
        },
        setFindChatBotStatus: (state, action) => {
            state.findChatBotStatus = action.payload
        },
        setFindChatBotByIdStatus: (state, action) => {
            state.findChatBotByIdStatus = action.payload
        },
        setQuestionBotStatus: (state, action) => {
            state.questionBotStatus = action.payload
        },
        setDeleteBotChatStatus: (state, action) => {
            state.deleteBotChatStatus = action.payload
        },
        setFindMessagesStatus: (state, action) => {
            state.findMessagesStatus = action.payload
        },
        setRequestUserIdFilterBot: (state, action) => {
            state.filterChatBot.request_user_id = action.payload
        },
        setRequestFinancialAdvisorUserIdFilterBot: (state, action) => {
            state.filterChatBot.financial_advisor_user_id = action.payload
        }
    },
    extraReducers(builder) {
        builder
            .addCase(findChats.pending, (state) => {
                state.findChatsStatus = 'loading'
            })
            .addCase(findChats.fulfilled, (state, action) => {
                state.findChatsStatus = 'successfully'
                state.findChatResponse = action.payload
            })
            .addCase(findChats.rejected, (state) => {
                state.findChatsStatus = 'failed'
            })
            .addCase(findMessages.pending, (state) => {
                state.findMessagesStatus = 'loading'
            })
            .addCase(findMessages.fulfilled, (state, action) => {
                state.findMessagesStatus = 'successfully'
                state.findMessagesResponse = action.payload
            })
            .addCase(findMessages.rejected, (state) => {
                state.findMessagesStatus = 'failed'
            })
            .addCase(setChatSeen.pending, (state) => {
                state.setChatSeenStatus = 'loading'
            })
            .addCase(setChatSeen.fulfilled, (state, action) => {
                state.setChatSeenStatus = 'successfully'
            })
            .addCase(setChatSeen.rejected, (state) => {
                state.setChatSeenStatus = 'failed'
            })
            .addCase(unSeenMessages.pending, (state) => {
                state.unSeenMessagesStatus = 'loading'
            })
            .addCase(unSeenMessages.fulfilled, (state, action) => {
                state.unSeenMessagesStatus = 'successfully'
                state.unSeenMessagesResponse = action.payload
            })
            .addCase(unSeenMessages.rejected, (state) => {
                state.unSeenMessagesStatus = 'failed'
            })
            .addCase(findLastMessage.pending, (state) => {
                state.findLastMessageStatus = 'loading'
            })
            .addCase(findLastMessage.fulfilled, (state, action) => {
                state.findLastMessageStatus = 'successfully'
                state.findLastMessageResponse = action.payload
            })
            .addCase(findLastMessage.rejected, (state) => {
                state.findLastMessageStatus = 'failed'
            })
            .addCase(findChatsByLoggedUser.fulfilled, (state, action) => {
                state.findChatsByLoggedUserStatus = 'successfully'
                state.findChatByLoggedUserResponse = action.payload
            })
            .addCase(findChatsByLoggedUser.pending, (state) => {
                state.findChatsByLoggedUserStatus = 'loading'
            })
            .addCase(findChatsByLoggedUser.rejected, (state, action) => {
                state.findChatsByLoggedUserStatus = 'failed'
            })
            .addCase(findChatBot.pending, (state) => {
                state.findChatBotStatus = 'loading'
            })
            .addCase(findChatBot.fulfilled, (state, action) => {
                state.findChatBotStatus = 'successfully'
                state.findChatBotResponse = action.payload
            })
            .addCase(findChatBot.rejected, (state) => {
                state.findChatBotStatus = 'failed'
            })
            .addCase(findChatBotById.pending, (state) => {
                state.findChatBotByIdStatus = 'loading'
            })
            .addCase(findChatBotById.fulfilled, (state, action) => {
                state.findChatBotByIdStatus = 'successfully'
                state.findChatBotByIdResponse = action.payload
            })
            .addCase(findChatBotById.rejected, (state) => {
                state.findChatBotByIdStatus = 'failed'
            })
            .addCase(questionBot.pending, (state) => {
                state.questionBotStatus = 'loading'
            })
            .addCase(questionBot.fulfilled, (state, action) => {
                state.questionBotStatus = 'successfully'
            })
            .addCase(questionBot.rejected, (state) => {
                state.questionBotStatus = 'failed'
            })
            .addCase(getAllBotTitles.pending, (state) => {
                state.getAllBotTitlesStatus = 'loading'
            })
            .addCase(getAllBotTitles.fulfilled, (state, action) => {
                state.getAllTitlesResponse = action.payload
                state.getAllBotTitlesStatus = 'successfully'
            })
            .addCase(getAllBotTitles.rejected, (state) => {
                state.getAllBotTitlesStatus = 'failed'
            })
           .addCase(deleteBotChat.pending, (state) => {
                state.deleteBotChatStatus= 'loading'
            })
            .addCase(deleteBotChat.fulfilled, (state, action) => {
                state.deleteBotChatStatus = 'successfully'
            })
            .addCase(deleteBotChat.rejected, (state) => {
                state.deleteBotChatStatus = 'failed'
            }) 
    },
})

export const {
    setSelectedChat,
    setChatList,
    setUsersListAfterSend,
    setChatSeenStatus,
    setFindChatsStatus,
    setFindChatsByLoggedUserStatus,
    setFindChatBotStatus,
    setFindChatBotByIdStatus,
    setQuestionBotStatus,
    setDeleteBotChatStatus,
    setFindMessagesStatus,
    setRequestFinancialAdvisorUserIdFilterBot,
    setRequestUserIdFilterBot
} = chatSlice.actions

export default chatSlice.reducer
